import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

export interface UserProfile {
  id: string;
  plan: 'free' | 'pro' | 'elite';
  credits_used: number;
  credits_total: number;
}

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const fetchProfile = async () => {
    if (!user) return;
    setLoadingProfile(true);
    
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("plan, credits_used, credits_total")
        .eq("id", user.id)
        .maybeSingle();

      if (error) {
        console.warn("Profile fetch error:", error);
      } else if (data) {
        setProfile(data as UserProfile);
      } else {
        // No profile found, let's create a default one
        const { data: newProfile, error: createError } = await supabase
          .from("profiles")
          .upsert({
            id: user.id,
            plan: 'free',
            credits_used: 0,
            credits_total: 5
          }, { onConflict: 'id' })
          .select()
          .single();
        
        if (!createError) {
          setProfile(newProfile as UserProfile);
          // Call the Brevo signup function
          if (user?.email) {
            supabase.functions.invoke("subscribe-brevo", { body: { email: user.email } }).catch(err => {
              console.error("Failed to add to Brevo list:", err);
            });
          }
        } else {
          console.error("Profile creation error:", createError);
        }
      }
    } catch (err) {
      console.error("useProfile catch:", err);
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (user) fetchProfile();
    else setProfile(null);
  }, [user]);

  return { profile, loadingProfile, refetchProfile: fetchProfile };
};
